import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Zap, LayoutDashboard, Link2, CreditCard, LogOut, Plus, Settings, ChevronDown, User } from 'lucide-react';
import MobileNav from './MobileNav';
import api from '../api';

export default function Layout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    api.get('/auth/me').then(r => { setUser(r.data); localStorage.setItem('user', JSON.stringify(r.data)); }).catch(() => {});
  }, []);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setShowProfile(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const logout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); };

  const nav = [
    { to: '/', icon: <LayoutDashboard size={16} />, label: 'Automations' },
    { to: '/integrations', icon: <Link2 size={16} />, label: 'Connections' },
    { to: '/billing', icon: <CreditCard size={16} />, label: 'Billing' },
    { to: '/settings', icon: <Settings size={16} />, label: 'Settings' },
  ];

  const tierColor = { free: 'var(--text3)', starter: 'var(--green)', growth: 'var(--accent3)', business: 'var(--yellow)' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile navigation */}
      <MobileNav user={user} onLogout={logout} />

      {/* Desktop Sidebar */}
      <aside style={{
        width: 248, background: 'var(--bg2)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100,
      }}>
        <div style={{ padding: '22px 20px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #6366f1, #a855f7)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px rgba(99,102,241,0.5)' }}>
              <Zap size={19} color="white" fill="white" />
            </div>
            <div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 20, letterSpacing: '0.05em', lineHeight: 1 }}>ASTRA</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text3)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>AUTOMATIONS</div>
            </div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 13, fontWeight: 700 }} onClick={() => navigate('/new')}>
            <Plus size={15} /> New Automation
          </button>
        </div>

        <nav style={{ flex: 1, padding: '16px 12px' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: 'var(--text3)', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '0 8px', marginBottom: 8 }}>Navigation</div>
          {nav.map(item => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, marginBottom: 3,
                textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 700 : 500,
                color: isActive ? 'white' : 'var(--text2)',
                background: isActive ? 'linear-gradient(135deg, rgba(99,102,241,0.9), rgba(79,70,229,0.9))' : 'transparent',
                boxShadow: isActive ? '0 4px 16px rgba(99,102,241,0.4)' : 'none',
                border: isActive ? '1px solid rgba(99,102,241,0.5)' : '1px solid transparent',
                transition: 'all 0.2s'
              })}>
              {item.icon} {item.label}
            </NavLink>
          ))}
        </nav>

        {user && (
          <div style={{ padding: '12px', borderTop: '1px solid var(--border)' }} ref={ref}>
            {showProfile && (
              <div style={{ position: 'absolute', bottom: 78, left: 12, right: 12, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 12, overflow: 'hidden', zIndex: 200, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{user.full_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>{user.email}</div>
                </div>
                {[
                  { label: 'Profile Settings', icon: <User size={14} />, action: () => { navigate('/settings'); setShowProfile(false); } },
                  { label: 'Billing & Plans', icon: <CreditCard size={14} />, action: () => { navigate('/billing'); setShowProfile(false); } },
                ].map(item => (
                  <button key={item.label} onClick={item.action} style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Geist', fontWeight: 600, textAlign: 'left', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg4)'; e.currentTarget.style.color = 'var(--text)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text2)'; }}>
                    {item.icon} {item.label}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <button onClick={logout} style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', color: 'var(--red)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Geist', fontWeight: 600, textAlign: 'left' }}>
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </div>
            )}
            <div onClick={() => setShowProfile(!showProfile)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', borderRadius: 10, cursor: 'pointer', background: showProfile ? 'var(--bg3)' : 'transparent', border: `1px solid ${showProfile ? 'var(--border2)' : 'transparent'}`, transition: 'all 0.2s' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--accent), var(--purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: 'white', boxShadow: '0 0 12px rgba(99,102,241,0.4)' }}>
                {user.full_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.full_name}</div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: tierColor[user.tier] || 'var(--text3)' }}>{user.tier} plan</div>
              </div>
              <ChevronDown size={13} color="var(--text3)" style={{ transform: showProfile ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="main-content" style={{ marginLeft: 248, flex: 1, padding: '40px 44px', minHeight: '100vh' }} className="fade-in main-content">
        <Outlet />
      </main>
    </div>
  );
}
